import { type NavItem } from "components/LeftNavV2/LeftNavDrawerV2";
import { GroupNavItem, StandardNavItem } from "components/LeftNavV2/NavItemV2";
import { useMemo } from "react";
import { useCustomerNavSectionsV2 } from "routes/Customer/customerNavSectionsV2";
import { useStaffNavSectionsV2 } from "routes/Staff/staffNavSectionsV2";

import { useCurrentUser } from "./useCurrentUser";
import { useTenant } from "./useTenant";

type LandingPageSection = {
  title: string;
  longTitle?: string;
  items: (StandardNavItem | GroupNavItem)[];
};

function isLandingPageNavItem(item: NavItem): item is StandardNavItem | GroupNavItem {
  return item.kind === "Standard" || item.kind === "Group";
}

export type LandingPageSections = {
  loading: boolean;
  sections: LandingPageSection[];
};

const customerSectionsToInclude: string[] = ["Programs", "Toolkit", "Catalogs"];

const staffSectionToInclude = "Staff";
const staffItemsToInclude: string[] = [
  "Fulfillment",
  "Organizations",
  "Agencies",
  "Mail Room",
  "Payments",
];

export function useLandingPageSections(): LandingPageSections {
  const { tenant } = useTenant();

  const { isStaff, loading: loadingUser } = useCurrentUser();
  const { navItems: sections } = useCustomerNavSectionsV2();
  const staffNavSections = useStaffNavSectionsV2();

  const customerSections = useMemo(() => {
    const includedSections = sections.filter((section) =>
      customerSectionsToInclude.includes(section.title)
    );
    return includedSections.map((section) => ({
      ...section,
      items: section.items.filter(isLandingPageNavItem),
    }));
  }, [sections]);

  // Show a subset of the staff tools on the landing page
  const filteredStaffSections: LandingPageSection[] = useMemo(() => {
    const sections = staffNavSections.filter((section) => section.title === staffSectionToInclude);

    return sections.map((section) => {
      return {
        ...section,
        items: section.items.filter(
          (item): item is StandardNavItem | GroupNavItem =>
            isLandingPageNavItem(item) && staffItemsToInclude.includes(item.text)
        ),
      };
    });
  }, [staffNavSections]);

  const navItems = useMemo(() => {
    if (!isStaff) {
      return customerSections;
    }

    if (tenant) {
      return [...customerSections, ...filteredStaffSections];
    } else {
      return filteredStaffSections;
    }
  }, [tenant, customerSections, filteredStaffSections, isStaff]);

  return {
    loading: loadingUser,
    sections: navItems,
  };
}
